import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { WEBApi } from '../../API/WEBApi';

interface ImageUpload {
  _id: string;
  filename: string;
  url: string;
  uploadedAt: string;
}

interface Props {
  onSelect: (url: string) => void;
  selectedUrl?: string;
}

const ImageSelector: React.FC<Props> = ({ onSelect, selectedUrl }) => {
  const [images, setImages] = useState<ImageUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(false);

  const fetchImages = async (currentPage = 1) => {
    setLoading(true);
    try {
      const res = await WEBApi.getUploadedImages(currentPage);
      const newImages: ImageUpload[] = res.images || [];
      const totalPages = res.totalPages || 1;

      if (currentPage === 1) {
        setImages(newImages);
      } else {
        const existingIds = new Set(images.map((img) => img._id));
        const filtered = newImages.filter((img) => !existingIds.has(img._id));
        setImages((prev) => [...prev, ...filtered]);
      }

      setHasMore(currentPage < totalPages);
    } catch (err) {
      console.error(err);
      setError('Failed to load images.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClick = () => {
    setShowSelector(true);
    if (images.length === 0) {
      fetchImages(1);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchImages(nextPage);
  };

  return (
    <div>
      {/* Image preview with overlay button */}
      <div
        className="position-relative mb-3"
        style={{ width: '100%', maxWidth: 300, height: 180, border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}
      >
        {selectedUrl && <img
          src={selectedUrl || 'https://via.placeholder.com/300x180?text=No+Image'}
          alt="Selected"
          className="w-100 h-100"
          style={{ objectFit: 'cover' }}
        />}
        <button
          type="button"
          className="btn btn-sm btn-dark position-absolute top-50 start-50 translate-middle"
          onClick={handleSelectClick}
        >
          Select Image
        </button>
      </div>

      {/* Image Selector */}
      {showSelector && (
        <>
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="row">
            {images.map((img) => (
              <div
                key={img._id}
                className="col-md-3 mb-3"
                style={{ cursor: 'pointer' }}
              >
                <div
                  className={`card ${selectedUrl === img.url ? 'border-primary' : ''}`}
                  onClick={() => {
                    onSelect(img.url);
                    setShowSelector(false);
                  }}
                >
                  <img
                    src={img.url}
                    alt={img.filename}
                    className="card-img-top"
                    style={{ height: '150px', objectFit: 'cover' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewImage(img.url);
                    }}
                  />
                  <div className="card-body p-2">
                    <p className="small text-center text-truncate mb-0">{img.filename}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && !loading && (
            <div className="text-center my-3">
              <button className="btn btn-outline-primary" onClick={handleLoadMore}>
                Load More
              </button>
            </div>
          )}

          {loading && (
            <div className="text-center my-3">
              <div className="spinner-border text-primary" />
            </div>
          )}
        </>
      )}

      {/* Preview Modal */}
      <Modal show={!!previewImage} onHide={() => setPreviewImage(null)} centered size="lg">
        <Modal.Body className="p-0">
          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              className="img-fluid w-100"
              style={{ objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ImageSelector;
